/**
 * Deployment API client.
 *
 * Base URL: /api/orchestration/deploy
 * All endpoints are system-wide (not project-scoped).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DeploymentStatus =
  | 'pending' | 'approved' | 'building' | 'deploying' | 'verifying'
  | 'success' | 'partial' | 'failed' | 'rolled_back' | 'cancelled';

export type TriggerType = 'push' | 'manual' | 'rollback' | 'schedule';
export type AutonomyTier = 'L1' | 'L2' | 'L3';

export interface DeploymentRun {
  id: string;
  environment: string;
  targetCommit: string;
  baseCommit: string;
  triggeredBy: TriggerType;
  approvedBy: string | null;
  status: DeploymentStatus;
  servicesChanged: string[];
  servicesDeployed: string[];
  servicesFailed: string[];
  migrationApplied: boolean;
  smokeTestPassed: boolean | null;
  smokeTestResult: Record<string, unknown> | null;
  errorMessage: string | null;
  durationMs: number | null;
  dryRun: boolean;
  workflowId: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

export interface ServiceResult {
  id: string;
  serviceName: string;
  status: string;
  previousImageTag: string | null;
  newImageTag: string | null;
  buildDurationMs: number | null;
  deployDurationMs: number | null;
  healthCheckPassed: boolean | null;
  errorLog: string | null;
}

export interface DeploymentDetail extends DeploymentRun {
  serviceResults: ServiceResult[];
}

export interface AgentTrust {
  agentId: string;
  trustScore: number;
  autonomyTier: AutonomyTier;
  totalDeployments: number;
  successfulDeployments: number;
  failedDeployments: number;
  consecutiveSuccesses: number;
  lastDeploymentId: string | null;
  lastDeploymentAt: string | null;
  promotedAt: string | null;
  demotedAt: string | null;
}

export interface TrustEvent {
  id: number;
  agentId: string;
  eventType: string;
  delta: number;
  newScore: number;
  newTier: string;
  deploymentId: string | null;
  reason: string | null;
  createdAt: string;
}

interface TriggerInput {
  environment: 'staging' | 'production';
  targetCommit?: string;
  baseCommit?: string;
  triggeredBy: TriggerType;
  services?: string[];
  dryRun?: boolean;
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

const BASE = '/api/orchestration/deploy';

class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${BASE}${endpoint}`;
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  if (response.status === 204) return null as T;
  return response.json();
}

export const deploymentApi = {
  listHistory: () => request<{ data: DeploymentRun[] }>('/history'),
  getStatus: (id: string) => request<{ data: DeploymentDetail }>(`/${id}/status`),
  triggerDeploy: (body: TriggerInput) =>
    request<{ data: { deploymentId: string; workflowId: string } }>('/trigger', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  approveDeploy: (id: string) =>
    request<{ data: { id: string; status: string } }>(`/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ approvedBy: 'dashboard-user' }),
    }),
  rollbackDeploy: (id: string) =>
    request<{ data: { deploymentId: string } }>(`/${id}/rollback`, { method: 'POST' }),
  getAgentTrust: () => request<{ data: AgentTrust }>('/agent/trust'),
  getTrustHistory: () => request<{ data: TrustEvent[] }>('/agent/trust/history'),
};

// ---------------------------------------------------------------------------
// React Query hooks
// ---------------------------------------------------------------------------

const KEYS = {
  deployments: ['deployments'] as const,
  deployment: (id: string) => ['deployments', id] as const,
  agentTrust: ['deployments', 'agent-trust'] as const,
  trustHistory: ['deployments', 'trust-history'] as const,
};

const ACTIVE_STATUSES: DeploymentStatus[] = ['pending', 'approved', 'building', 'deploying', 'verifying'];

export function useDeployments() {
  return useQuery({
    queryKey: KEYS.deployments,
    queryFn: () => deploymentApi.listHistory(),
    refetchInterval: (query) => {
      const data = query.state.data?.data;
      const hasActive = data?.some(d => ACTIVE_STATUSES.includes(d.status));
      return hasActive ? 5_000 : 15_000;
    },
  });
}

export function useDeploymentStatus(id: string | null) {
  return useQuery({
    queryKey: KEYS.deployment(id!),
    queryFn: () => deploymentApi.getStatus(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.data?.status;
      if (status && ACTIVE_STATUSES.includes(status)) return 5_000;
      return false;
    },
  });
}

export function useTriggerDeploy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TriggerInput) => deploymentApi.triggerDeploy(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.deployments });
    },
  });
}

export function useApproveDeploy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deploymentApi.approveDeploy(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: KEYS.deployment(id) });
      queryClient.invalidateQueries({ queryKey: KEYS.deployments });
    },
  });
}

export function useRollbackDeploy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deploymentApi.rollbackDeploy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.deployments });
    },
  });
}

export function useAgentTrust() {
  return useQuery({
    queryKey: KEYS.agentTrust,
    queryFn: () => deploymentApi.getAgentTrust(),
    refetchInterval: 60_000,
  });
}

export function useTrustHistory() {
  return useQuery({
    queryKey: KEYS.trustHistory,
    queryFn: () => deploymentApi.getTrustHistory(),
  });
}
